import React, { useState } from 'react';
import axios from 'axios';
import {
  Building2,
  Wrench,
  Hammer,
  Home as HomeIcon,
  Triangle,
  Phone,
  Award,
  Shield,
  CheckCircle,
  Star,
  MapPin,
  Clock,
  Mail,
  Menu,
  X,
  ChevronRight,
  Quote,
  Square,
  Layers,
  Paintbrush,
  Box,
  DoorOpen,
  Ruler,
  Blocks,
  PanelTop,
  Fence,
  Trash2,
  TreePine,
  Droplet,
  Wind,
  Factory,
  Flame,
  Lightbulb,
  User,
  Store,
  Truck,
  Container,
  Snowflake,
  Warehouse
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { useToast } from '../hooks/use-toast';
import { services, testimonials, galleryImages, certifications, serviceAreas, teamMembers, faqs } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Building2,
  Wrench,
  Hammer,
  Home: HomeIcon,
  Triangle,
  Phone,
  Award,
  Shield,
  CheckCircle,
  Star,
  Square,
  Layers,
  Paintbrush,
  Box,
  DoorOpen,
  Ruler,
  Blocks,
  PanelTop,
  Fence,
  Trash2,
  TreePine,
  Droplet,
  Wind,
  Factory,
  Flame,
  Lightbulb,
  Store,
  Truck,
  Container,
  Snowflake,
  Warehouse
};

export const Home = () => {
  const { toast } = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Call the backend API
      const response = await axios.post(`${API}/quotes`, formData);
      
      if (response.data) {
        toast({
          title: "Quote Request Submitted!",
          description: "We've received your request and will contact you within 24 hours.",
        });
        
        // Clear the form
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your request. Please call us at (317) 376-5051.",
        variant: "destructive"
      });
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Contact Bar */}
      <div className="fixed top-0 w-full bg-gradient-to-r from-slate-900 to-black text-white z-50 border-b border-blue-500/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between py-2 text-sm">
            <div className="flex items-center gap-4 mb-2 sm:mb-0">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-blue-400" />
                <a href="tel:317-376-5051" className="hover:text-blue-400 transition-colors font-medium">
                  (317) 376-5051
                </a>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <MapPin className="h-4 w-4 text-blue-400" />
                <span className="text-slate-300">2 N Main ST, Brooklyn, IN 46111</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <Clock className="h-4 w-4 text-blue-400" />
              <span className="text-xs">24/7 Emergency Service Available</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="fixed top-10 sm:top-12 w-full bg-gradient-to-r from-slate-800 to-slate-900 backdrop-blur-md shadow-2xl z-50 border-b border-blue-500/20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center space-x-4 group cursor-pointer">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl blur-sm opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <div className="relative bg-white p-2 rounded-xl shadow-lg transform group-hover:scale-105 transition-transform">
                  <img 
                    src="https://lh3.googleusercontent.com/pw/AP1GczMPyP8bJ7C2vwXj3StmSDvOwzC8va7LrJBGPGGjVvrDHT6_6MyyhY1UcsTlHCQXMz7P_sIwR2BQ2NtR2JoGGqIHo1EYrD_6TpQKBHT8F5m23Y-5DLX1=w400-h200-no"
                    alt="HoosYer Helper Logo"
                    className="h-14 w-auto object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              <button 
                onClick={() => scrollToSection('home')} 
                className="px-4 py-2 text-slate-300 hover:text-blue-400 hover:bg-slate-800 rounded-lg transition-all font-medium"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('services')} 
                className="px-4 py-2 text-slate-300 hover:text-blue-400 hover:bg-slate-800 rounded-lg transition-all font-medium"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('about')} 
                className="px-4 py-2 text-slate-300 hover:text-blue-400 hover:bg-slate-800 rounded-lg transition-all font-medium"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('testimonials')} 
                className="px-4 py-2 text-slate-300 hover:text-blue-400 hover:bg-slate-800 rounded-lg transition-all font-medium"
              >
                Testimonials
              </button>
              <Button 
                onClick={() => scrollToSection('contact')} 
                className="ml-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all border border-blue-500/30"
              >
                Get Free Quote
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2.5 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="lg:hidden py-4 space-y-2 border-t border-slate-700">
              <button 
                onClick={() => scrollToSection('home')} 
                className="block w-full text-left px-4 py-2 text-slate-300 hover:text-blue-400 hover:bg-slate-800 rounded-lg transition-colors font-medium"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('services')} 
                className="block w-full text-left px-4 py-2 text-slate-300 hover:text-blue-400 hover:bg-slate-800 rounded-lg transition-colors font-medium"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('about')} 
                className="block w-full text-left px-4 py-2 text-slate-300 hover:text-blue-400 hover:bg-slate-800 rounded-lg transition-colors font-medium"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('testimonials')} 
                className="block w-full text-left px-4 py-2 text-slate-300 hover:text-blue-400 hover:bg-slate-800 rounded-lg transition-colors font-medium"
              >
                Testimonials
              </button>
              <div className="px-4">
                <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
                  <MapPin className="h-4 w-4 text-blue-500" />
                  <span>2 N Main ST, Brooklyn, IN 46111</span>
                </div>
              </div>
              <Button 
                onClick={() => scrollToSection('contact')} 
                className="w-full mx-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
              >
                Get Free Quote
              </Button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-44 sm:pt-48 pb-20 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Your Trusted Construction Partner
              </h2>
              <p className="text-xl text-slate-300 mb-8">
                Full-service construction, excavation, and handyman services. From ground-up builds to small repairs, we do it all.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={() => scrollToSection('contact')} size="lg" className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-lg px-8 shadow-lg border border-blue-500/30">
                  Get Free Quote
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-blue-500 text-blue-400 hover:bg-slate-800 text-lg px-8">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (317) 376-5051
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-slate-300">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-400" />
                  <span>Licensed & Insured</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-400" />
                  <span>24/7 Emergency</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-blue-600/20 rounded-2xl blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5"
                alt="Construction team"
                className="rounded-2xl shadow-2xl relative border-2 border-blue-500/30"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Services Banner */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 py-6 border-y border-blue-500/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-white">
              <Phone className="h-6 w-6" />
              <span className="text-lg font-semibold">24/7 Emergency Services Available</span>
            </div>
            <Button variant="outline" className="bg-white text-blue-700 hover:bg-slate-100 border-0 font-semibold shadow-lg">
              Call Now: (317) 376-5051
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 bg-gradient-to-br from-slate-900 to-black">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Services</h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Comprehensive construction and handyman solutions for all your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="hover:shadow-xl transition-all border-slate-700 bg-slate-800 overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-slate-900/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center shadow-lg border border-blue-500/30">
                          <IconComponent className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-white">{service.title}</h3>
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardDescription className="text-slate-300 mt-2">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-slate-300">
                          <CheckCircle className="h-4 w-4 text-blue-400 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 bg-gradient-to-br from-slate-900 to-black">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">What Our Clients Say</h2>
            <p className="text-xl text-slate-300">Don't just take our word for it</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-slate-700 bg-slate-800">
                <CardHeader>
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`h-5 w-5 ${
                          i < testimonial.rating 
                            ? 'fill-blue-400 text-blue-400' 
                            : 'text-slate-600'
                        }`} 
                      />
                    ))}
                  </div>
                  <Quote className="h-8 w-8 text-slate-600 mb-2" />
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300 mb-4">{testimonial.text}</p>
                  <div className="border-t border-slate-700 pt-4">
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-slate-400">{testimonial.location}</p>
                    <p className="text-sm text-blue-400 mt-1">{testimonial.service}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About & Certifications Section */}
      <section id="about" className="py-20 px-4 bg-slate-800">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">About HoosYer Helper</h2>
            <p className="text-2xl text-blue-400 font-semibold mb-2">Your Only Number You'll Ever Need</p>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              A full-service construction and handyman company serving all of Indiana for over 30 years
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <h3 className="text-3xl font-bold text-white mb-6">Who We Are</h3>
              <p className="text-lg text-slate-300 mb-4">
                With over 30 years of experience serving all of Indiana, HoosYer Helper LLC has built an unmatched reputation for quality workmanship, reliability, and complete customer satisfaction. We pride ourselves on being a truly full-service construction and handyman company—the only number you'll ever need for any project, big or small.
              </p>
              <p className="text-lg text-slate-300 mb-4">
                HoosYer Helper isn't just another contractor—we're your complete construction solution. From massive commercial builds and ground-up residential construction to the smallest repair jobs, we handle it all with the same level of professionalism, expertise, and attention to detail. Our comprehensive capabilities mean you never have to search for multiple contractors again.
              </p>
              <p className="text-lg text-slate-300 mb-6">
                We understand that construction needs come in all shapes and sizes, which is why we've built a company that can tackle anything. Whether you're planning a multi-million dollar commercial project or need a simple handyman repair, our team brings the same commitment to excellence, backed by comprehensive insurance and industry-leading warranties.
              </p>

              <h3 className="text-2xl font-bold text-white mb-4 mt-8">The Full Scope of Our Capabilities</h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-semibold">Large-Scale Commercial Construction</p>
                    <p className="text-slate-400 text-sm">Multi-story buildings, restaurants, gas stations, warehouses, and commercial complexes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-semibold">Residential Ground-Up Construction</p>
                    <p className="text-slate-400 text-sm">Custom homes, additions, major renovations from foundation to final finishes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-semibold">Heavy Equipment & Excavation</p>
                    <p className="text-slate-400 text-sm">Site development, land clearing, grading, foundation work, utility installation</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-semibold">Specialized Trade Work</p>
                    <p className="text-slate-400 text-sm">Welding, sandblasting, commercial coatings, custom fabrication, masonry</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-semibold">Complete Remodeling Services</p>
                    <p className="text-slate-400 text-sm">Kitchens, bathrooms, basements, whole-home transformations</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-semibold">Everyday Handyman Services</p>
                    <p className="text-slate-400 text-sm">Small repairs, installations, maintenance, odd jobs—no job too small</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-semibold">Emergency Response Services</p>
                    <p className="text-slate-400 text-sm">24/7 availability for storm damage, water damage, and urgent repairs</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="mb-8">
                <img
                  src="https://images.unsplash.com/photo-1694521787193-9293daeddbaa"
                  alt="Professional consultation"
                  className="rounded-2xl shadow-xl border-2 border-blue-500/30 mb-6"
                />
              </div>

              <h3 className="text-2xl font-bold text-white mb-4">Why Choose HoosYer Helper?</h3>
              <div className="bg-slate-900 p-6 rounded-xl border border-slate-700 mb-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Award className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">One Company, Complete Solutions</p>
                      <p className="text-slate-400 text-sm">From major construction projects to minor repairs, we do it all. Save time, money, and hassle by working with one trusted company for all your needs.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Shield className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">30 Years of Proven Excellence</p>
                      <p className="text-slate-400 text-sm">Three decades of satisfied clients, successful projects, and reputation built on quality, integrity, and exceptional service.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Fully Licensed & Insured</p>
                      <p className="text-slate-400 text-sm">Complete licensing, comprehensive insurance, and bonding for your protection and peace of mind on every project.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Building2 className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Statewide Mobile Service</p>
                      <p className="text-slate-400 text-sm">We bring our expertise, equipment, and materials to you anywhere in Indiana. No project too far, no location too remote.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Star className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">No Idea Too Ambitious</p>
                      <p className="text-slate-400 text-sm">Custom designs, unique fabrications, challenging projects—we thrive on bringing your vision to life, no matter how complex.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-6 rounded-xl border border-blue-500/30">
                <h4 className="text-xl font-bold text-white mb-3">The HoosYer Helper Promise</h4>
                <p className="text-slate-100 mb-4">
                  When you call HoosYer Helper, you're getting more than just a contractor—you're getting a partner committed to your success. We don't just complete projects; we build lasting relationships based on trust, quality, and exceptional results.
                </p>
                <p className="text-slate-100 font-semibold">
                  One call. One company. Every solution. That's the HoosYer Helper difference.
                </p>
              </div>
            </div>
          </div>

          {/* Certifications */}
          <div className="grid md:grid-cols-4 gap-6">
            {certifications.map((cert) => {
              const IconComponent = iconMap[cert.icon];
              return (
                <Card key={cert.id} className="text-center border-slate-700 bg-slate-800">
                  <CardHeader>
                    <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center mb-4 border border-blue-500/30">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-lg text-white">{cert.name}</CardTitle>
                    <CardDescription className="text-slate-400">{cert.issuer}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Leadership Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-900 to-black">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Leadership</h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Meet the dedicated team behind HoosYer Helper's 30 years of excellence
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {teamMembers.map((member) => (
              <Card key={member.id} className="border-slate-700 bg-slate-800 overflow-hidden hover:shadow-2xl transition-shadow">
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="h-16 w-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center border-2 border-blue-500/30">
                        <User className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white">{member.name}</h3>
                        <p className="text-blue-400 font-semibold">{member.title}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <CardContent className="p-6">
                  <div className="mb-6">
                    <p className="text-slate-300 leading-relaxed mb-4">
                      {member.bio}
                    </p>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Award className="h-5 w-5 text-blue-400" />
                      Key Achievements
                    </h4>
                    <ul className="space-y-2">
                      {member.achievements.map((achievement, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-slate-300">
                          <CheckCircle className="h-4 w-4 text-blue-400 flex-shrink-0" />
                          <span className="text-sm">{achievement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="border-t border-slate-700 pt-4">
                    <Quote className="h-6 w-6 text-slate-600 mb-2" />
                    <p className="text-slate-400 italic text-sm">
                      "{member.quote}"
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-slate-900 to-black">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Service Areas</h2>
            <p className="text-lg text-slate-300">Proudly serving all of Indiana - statewide coverage for all your construction needs</p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {serviceAreas.map((area, idx) => (
              <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-slate-800 to-slate-700 rounded-full border border-blue-500/30">
                <MapPin className="h-4 w-4 text-blue-400" />
                <span className="text-slate-200 font-medium">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-slate-800">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-slate-800 border border-slate-700 rounded-lg px-6">
                <AccordionTrigger className="text-left text-white hover:text-blue-400">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-300">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact & Quote Form Section */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-br from-slate-900 to-black">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Get Your Free Quote</h2>
            <p className="text-xl text-slate-300">Tell us about your project and we'll get back to you within 24 hours</p>
          </div>

          <Card className="border-slate-700 bg-slate-800">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Name *</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your name"
                      className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Phone *</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="(555) 123-4567"
                      className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your@email.com"
                    className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Service Needed *</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.title}>{service.title}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Project Details *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    placeholder="Tell us about your project..."
                    rows={6}
                    className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400"
                  />
                </div>

                <Button type="submit" size="lg" className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg border border-blue-500/30">
                  Request Free Quote
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="text-center">
              <div className="h-12 w-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 border border-blue-500/30">
                <Phone className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Call Us</h3>
              <a href="tel:317-376-5051" className="text-blue-600 hover:text-blue-700 font-medium">
                (317) 376-5051
              </a>
            </div>
            <div className="text-center">
              <div className="h-12 w-12 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3 border border-slate-600">
                <Mail className="h-6 w-6 text-slate-300" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Email Us</h3>
              <p className="text-slate-600">info@hoosyerhelper.com</p>
            </div>
            <div className="text-center">
              <div className="h-12 w-12 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3 border border-slate-700">
                <MapPin className="h-4 w-4 text-slate-300" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Visit Us</h3>
              <p className="text-slate-600">2 N Main ST</p>
              <p className="text-slate-600 text-sm">Brooklyn, IN 46111</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-black via-slate-900 to-slate-800 text-white py-12 px-4 border-t border-blue-500/20">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-white p-2 rounded-lg">
                  <img 
                    src="https://lh3.googleusercontent.com/pw/AP1GczMPyP8bJ7C2vwXj3StmSDvOwzC8va7LrJBGPGGjVvrDHT6_6MyyhY1UcsTlHCQXMz7P_sIwR2BQ2NtR2JoGGqIHo1EYrD_6TpQKBHT8F5m23Y-5DLX1=w300-h150-no"
                    alt="HoosYer Helper Logo"
                    className="h-12 w-auto object-contain"
                  />
                </div>
              </div>
              <p className="text-slate-400 text-sm">
                Your trusted partner for construction, excavation, and handyman services throughout Indiana for over 30 years.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="hover:text-blue-400 transition-colors cursor-pointer">Construction</li>
                <li className="hover:text-blue-400 transition-colors cursor-pointer">Excavation</li>
                <li className="hover:text-blue-400 transition-colors cursor-pointer">Handyman</li>
                <li className="hover:text-blue-400 transition-colors cursor-pointer">Remodeling</li>
                <li className="hover:text-blue-400 transition-colors cursor-pointer">Emergency Services</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="hover:text-slate-300 transition-colors cursor-pointer">About Us</li>
                <li className="hover:text-slate-300 transition-colors cursor-pointer">Our Work</li>
                <li className="hover:text-slate-300 transition-colors cursor-pointer">Testimonials</li>
                <li className="hover:text-slate-300 transition-colors cursor-pointer">Certifications</li>
                <li className="hover:text-slate-300 transition-colors cursor-pointer">Service Areas</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-blue-400" />
                  <a href="tel:317-376-5051" className="hover:text-blue-400 transition-colors">
                    (317) 376-5051
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-400" />
                  info@hoosyerhelper.com
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-blue-400" />
                  <span>2 N Main ST<br />Brooklyn, IN 46111</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-400">
            <p>&copy; {new Date().getFullYear()} HoosYer Helper LLC. All rights reserved. | Licensed & Insured</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;